import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import type { User } from '@core/account-types';
import { I18nService } from '@core/i18n-legacy';
import type { SelectOption } from '@core/user-interface-legacy';
import { AvatarComponent, SelectComponent } from '@core/user-interface-legacy';

import { Account } from '../../../client/integrations-client.types';
import { CoreApiService, Destroyable, takeUntilDestroyed } from '../../../common';
import type { DynamicAdminSelectInputConfig } from '../../types';
import { DynamicFormInput } from '../dynamic-form/dynamic-form-input/dynamic-form-input';

export class AdminSelectInput extends DynamicFormInput<DynamicAdminSelectInputConfig> {
  public get hasSearch(): boolean {
    return !!this.config.hasSearch;
  }

  public get includeEmptyOption(): boolean {
    return !this.config.required;
  }

  public isHidden(): boolean {
    return !this.config.required && !!this.config.hideIfEmpty;
  }
}

@Component({
  selector: 'rti-admin-select-input',
  templateUrl: './admin-select-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, AvatarComponent, SelectComponent],
})
@Destroyable()
export class AdminSelectInputComponent {
  private readonly coreApi = inject(CoreApiService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly i18n = inject(I18nService);
  private readonly account = inject(Account);

  public readonly input = input<AdminSelectInput>(undefined!);

  public isLoading: boolean;
  public currentAdminId: number;
  public resourceSelectOptions: Array<SelectOption<number>>;

  public ngOnInit(): void {
    this.currentAdminId = this.input().config.value || this.account.admin.id;
    this.isLoading = true;
    this.cdRef.detectChanges();

    this.coreApi
      .get('/admins')
      .pipe(takeUntilDestroyed(this))
      .subscribe((data: { admins: User[] }) => {
        this.resourceSelectOptions = data.admins.map(admin => ({
          ...admin,
          value: admin.id,
          label: `${admin.firstName} ${admin.lastName}`,
        }));

        this.maybePreselectInputValue();

        this.isLoading = false;

        this.cdRef.detectChanges();
      });
  }

  public get selectPlaceholder(): string | undefined {
    return this.isLoading ? this.i18n.translate('rti.common.loading') : undefined;
  }

  private maybePreselectInputValue(): void {
    const initialOption = this.resourceSelectOptions.find(
      option => option.value === this.currentAdminId,
    );

    if (!initialOption) return;

    this.input().control.setValue(initialOption.value, { emitEvent: false });
  }
}
